import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';

/**
 * Every marketing page is the same shell: metadata, the fixed header, the
 * content, the footer. Pages differ in what they put between them.
 */
const PageLayout = ({ title, description, path, children }) => (
  <div className="page-shell">
    <SEO title={title} description={description} path={path} />
    <Navbar />
    <main>{children}</main>
    <Footer />
  </div>
);

/**
 * The banded page header. `art` takes an illustration for the pages that
 * lead with one; without it the header is a centred column.
 *
 * Decoration budget is two to three marks per surface — an orb, a shape,
 * and the dotted grid — so the header stays texture rather than noise.
 */
export const PageHero = ({
  eyebrow,
  accent = 'accent-brand',
  title,
  sub,
  actions,
  art: Art,
  children,
}) => (
  <section className="has-orbs relative overflow-hidden bg-surface pt-[72px]">
    {/* Same pale-blob motif as the home hero, so every sub-page reads as
        the same design language. */}
    <span className="blob blob--lg -left-48 -top-56" aria-hidden="true" />
    <span className="blob blob--md blob--soft -right-28 top-8" aria-hidden="true" />
    <span className="orb orb--brand orb--md orb--tl" aria-hidden="true" />
    <span className="orb orb--violet orb--sm orb--br" aria-hidden="true" />
    <span className="shape shape--ring shape--md shape--tr accent-6" aria-hidden="true" />

    <div className={`container-page relative py-14 lg:py-20 ${Art ? '' : 'text-center'}`}>
      <div className={Art ? 'grid items-center gap-12 lg:grid-cols-2' : 'mx-auto max-w-3xl'}>
        <div>
          {eyebrow && (
            <span className={`eyebrow ${accent} animate-fade-up`}>
              <span className="spark-mark" aria-hidden="true" />
              {eyebrow}
            </span>
          )}

          <h1
            className="mt-5 animate-fade-up text-section font-bold leading-[1.08] tracking-tight text-content"
            style={{ animationDelay: '60ms' }}
          >
            {title}
          </h1>

          {sub && (
            <p
              className={`mt-5 animate-fade-up text-lede leading-relaxed text-content-muted ${
                Art ? 'max-w-xl' : 'mx-auto max-w-2xl'
              }`}
              style={{ animationDelay: '120ms' }}
            >
              {sub}
            </p>
          )}

          {actions && (
            <div
              className={`mt-8 flex animate-fade-up flex-col gap-3 sm:flex-row ${
                Art ? '' : 'justify-center'
              }`}
              style={{ animationDelay: '180ms' }}
            >
              {actions}
            </div>
          )}

          {children}
        </div>

        {Art && (
          <div className={`${accent} card card--tint animate-fade-in overflow-hidden`}>
            <Art className="h-auto w-full" />
          </div>
        )}
      </div>
    </div>
  </section>
);

export default PageLayout;
